import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { fmtFR } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Pencil, Copy, Package, Calendar as CalIcon, Boxes, ExternalLink, History, RotateCcw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { MONTHS_FR, DAYS_FR, toISODate, addDays, pad } from "@/lib/time";
import { buildLoanGoogleCalendarUrl } from "@/lib/gcal";

const CONDITIONS = [
  { v: "neuf", label: "Neuf", cls: "bg-secondary text-secondary-foreground border-secondary" },
  { v: "bon", label: "Bon état", cls: "bg-primary/10 text-primary border-primary/30" },
  { v: "use", label: "Usé", cls: "bg-accent/40 text-accent-foreground border-accent" },
  { v: "hs", label: "HS", cls: "bg-destructive/10 text-destructive border-destructive/30" },
];

const LOAN_STATUS = {
  planned: { label: "Prévu", cls: "bg-muted text-muted-foreground border-border" },
  out: { label: "Sorti", cls: "bg-primary/10 text-primary border-primary/40" },
  returned: { label: "Retourné", cls: "bg-secondary text-secondary-foreground border-secondary" },
  cancelled: { label: "Annulé", cls: "bg-destructive/10 text-destructive border-destructive/30" },
};

// Catégories AAE — chaque catégorie définit ses propres champs additionnels
const EQUIPMENT_CATEGORIES = [
  "Matériel",
  "Mascotte",
  "Décoration",
  "Matériel audiovisuel",
  "Matériel d'animation",
  "Matériel électrique",
  "Consommables",
];

const DIM_LWH = [
  { key: "length_cm", label: "Longueur (cm)", type: "number", step: "0.1" },
  { key: "width_cm",  label: "Largeur (cm)",  type: "number", step: "0.1" },
  { key: "height_cm", label: "Hauteur (cm)",  type: "number", step: "0.1" },
];
const DIM_LWHD = [
  ...DIM_LWH,
  { key: "diameter_cm", label: "Diamètre (cm)", type: "number", step: "0.1" },
];
const POWER = { key: "power_w", label: "Puissance électrique (W)", type: "number", step: "1" };

const CATEGORY_FIELDS = {
  "Matériel": DIM_LWHD,
  "Mascotte": [
    { key: "hands_count", label: "Nombre de mains", type: "number", step: "1" },
    { key: "feet_count",  label: "Nombre de pieds", type: "number", step: "1" },
    { key: "has_pants",   label: "Pantalon",     type: "checkbox" },
    { key: "has_top",     label: "Haut",         type: "checkbox" },
    { key: "has_jacket",  label: "Veste",        type: "checkbox" },
    { key: "has_hat",     label: "Chapeau",      type: "checkbox" },
    { key: "has_belly",   label: "Faux ventre",  type: "checkbox" },
    { key: "has_frame",   label: "Armature",     type: "checkbox" },
    { key: "has_accessories", label: "Accessoires", type: "checkbox" },
    { key: "accessories_details", label: "Préciser les accessoires", type: "text", colSpan: 2, showIf: "has_accessories" },
  ],
  "Décoration": [
    { key: "color", label: "Couleur", type: "text" },
    ...DIM_LWH,
  ],
  "Matériel audiovisuel": [POWER, ...DIM_LWH],
  "Matériel d'animation": [POWER, ...DIM_LWH],
  "Matériel électrique":  [POWER, ...DIM_LWH],
  "Consommables": [
    { key: "weight_kg",   label: "Poids (kg)",           type: "number", step: "0.01" },
    { key: "expiry_date", label: "Date de péremption",   type: "date" },
  ],
};

const EMPTY_EQ = { name: "", category: "", quantity: 1, dimensions: "", serial_number: "", condition: "bon", value: 0, supplier: "", photo_url: "", notes: "", attributes: {} };
const EMPTY_LOAN = { equipment_id: "", type: "pret", borrower_name: "", borrower_contact: "", start_date: "", end_date: "", quantity: 1, daily_rate: 0, event_id: "", status: "planned", notes: "" };

// Résume les dimensions (nouvelles attributes L/l/H/D + fallback ancien champ dimensions)
const eqDimensionsLabel = (eq) => {
  const a = eq.attributes || {};
  const parts = [];
  const nums = ["length_cm", "width_cm", "height_cm"].map(k => a[k]).filter(v => v !== undefined && v !== null && v !== "");
  if (nums.length) parts.push(nums.join(" × ") + " cm");
  if (a.diameter_cm) parts.push(`⌀ ${a.diameter_cm} cm`);
  if (parts.length) return parts.join(" · ");
  return eq.dimensions || "";
};

function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

function monthGrid(monthDate) {
  // Return 6x7 grid of dates covering the month
  const first = firstOfMonth(monthDate);
  const day = (first.getDay() + 6) % 7; // 0=monday
  const start = new Date(first);
  start.setDate(first.getDate() - day);
  return Array.from({ length: 42 }).map((_, i) => addDays(start, i));
}

export default function EquipmentPage() {
  const [tab, setTab] = useState("inventory");
  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loans, setLoans] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [calCatFilter, setCalCatFilter] = useState("all");
  const [monthDate, setMonthDate] = useState(firstOfMonth(new Date()));

  const [eqDialogOpen, setEqDialogOpen] = useState(false);
  const [eqForm, setEqForm] = useState(EMPTY_EQ);
  const [eqEditingId, setEqEditingId] = useState(null);

  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [loanForm, setLoanForm] = useState(EMPTY_LOAN);
  const [loanEditingId, setLoanEditingId] = useState(null);
  const [stockDate, setStockDate] = useState(toISODate(new Date()));
  const [stockEndDate, setStockEndDate] = useState("");

  const loadEquipment = async () => {
    try {
      const params = {};
      if (catFilter !== "all") params.category = catFilter;
      if (q) params.q = q;
      const [{ data }, { data: cats }] = await Promise.all([
        api.get("/equipment", { params }),
        api.get("/equipment/categories"),
      ]);
      setEquipment(data);
      setCategories(cats);
    } catch (e) { toast.error(extractError(e)); }
  };

  const loadLoans = async () => {
    try {
      const first = firstOfMonth(monthDate);
      const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
      const params = { from_: toISODate(first), to: toISODate(last) };
      if (calCatFilter !== "all") params.category = calCatFilter;
      const [{ data }, evs] = await Promise.all([
        api.get("/loans", { params }),
        api.get("/events").catch(() => ({ data: [] })),
      ]);
      setLoans(data);
      setEvents(evs.data);
    } catch (e) { toast.error(extractError(e)); }
  };

  useEffect(() => { loadEquipment(); /* eslint-disable-next-line */ }, [q, catFilter]);
  useEffect(() => { loadLoans(); /* eslint-disable-next-line */ }, [monthDate, calCatFilter]);

  const stockAtDate = useMemo(() => {
    // Compute stock per equipment for the selected date OR range (start..end).
    // For a range: `out` = MAX sum of overlapping active loans on any single day in the range.
    if (!stockDate) return [];
    const start = stockDate;
    const end = stockEndDate && stockEndDate >= stockDate ? stockEndDate : stockDate;
    // Enumerate at most ~400 days to keep it cheap
    const days = [];
    let cur = new Date(start + "T00:00:00");
    const stop = new Date(end + "T00:00:00");
    let guard = 0;
    while (cur <= stop && guard++ < 400) {
      days.push(toISODate(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    return equipment
      .filter(eq => calCatFilter === "all" || eq.category === calCatFilter)
      .map(eq => {
        const active = loans.filter(l =>
          l.equipment_id === eq.id &&
          l.status !== "returned" && l.status !== "cancelled" &&
          l.start_date && l.end_date &&
          l.start_date <= end && l.end_date >= start
        );
        let maxOut = 0;
        let peakLoans = [];
        for (const day of days) {
          const dayLoans = active.filter(l => l.start_date <= day && day <= l.end_date);
          const dayOut = dayLoans.reduce((s, l) => s + (Number(l.quantity) || 1), 0);
          if (dayOut > maxOut) { maxOut = dayOut; peakLoans = dayLoans; }
        }
        return {
          eq,
          out: maxOut,
          available: Math.max(0, (Number(eq.quantity) || 0) - maxOut),
          loans: peakLoans,
        };
      })
      .sort((a, b) => (a.eq.category || "").localeCompare(b.eq.category || "") || a.eq.name.localeCompare(b.eq.name));
  }, [equipment, loans, stockDate, stockEndDate, calCatFilter]);

  const openNewEq = () => { setEqForm({ ...EMPTY_EQ, attributes: {} }); setEqEditingId(null); setEqDialogOpen(true); };
  const openEditEq = (eq) => {
    setEqForm({ ...EMPTY_EQ, ...eq, attributes: eq.attributes || {} });
    setEqEditingId(eq.id);
    setEqDialogOpen(true);
  };
  const duplicateEq = (eq) => {
    // Copie toutes les données sauf id/created_by/created_at, suffixe le nom
    const { id, created_by, created_at, ...rest } = eq;
    setEqForm({
      ...EMPTY_EQ,
      ...rest,
      name: `${eq.name || ""} (copie)`,
      attributes: eq.attributes ? { ...eq.attributes } : {},
    });
    setEqEditingId(null);
    setEqDialogOpen(true);
  };

  // Met à jour un champ dans attributes
  const setAttr = (key, value) => setEqForm(f => ({ ...f, attributes: { ...(f.attributes || {}), [key]: value } }));

  const saveEq = async () => {
    try {
      if (!eqForm.name?.trim()) { toast.error("Nom obligatoire"); return; }
      // Nettoie les attributs non pertinents pour la catégorie sélectionnée (évite les résidus si on change de catégorie)
      const fields = CATEGORY_FIELDS[eqForm.category] || [];
      const cleanAttrs = {};
      fields.forEach(f => {
        const v = (eqForm.attributes || {})[f.key];
        if (v === "" || v === undefined || v === null) return;
        if (f.type === "number") cleanAttrs[f.key] = parseFloat(v);
        else cleanAttrs[f.key] = v;
      });
      const payload = {
        ...eqForm,
        quantity: parseInt(eqForm.quantity) || 1,
        value: parseFloat(eqForm.value) || 0,
        attributes: cleanAttrs,
      };
      if (eqEditingId) { await api.patch(`/equipment/${eqEditingId}`, payload); toast.success("Matériel modifié"); }
      else { await api.post("/equipment", payload); toast.success("Matériel ajouté"); }
      setEqDialogOpen(false);
      loadEquipment();
      loadLoans();
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeEq = async (eq) => {
    // Récupère les prêts liés directement en base (les prêts hors mois courant ne sont pas dans `loans`)
    let attached = [];
    try {
      const { data } = await api.get("/loans", { params: { equipment_id: eq.id } });
      attached = data || [];
    } catch (_) { /* fallback silencieux */ }
    const active = attached.filter(l => l.status !== "returned" && l.status !== "cancelled");
    const upcoming = active.filter(l => !l.end_date || l.end_date >= toISODate(new Date()));
    let msg = `Supprimer "${eq.name}" ?`;
    if (attached.length) {
      msg += `\n\n⚠️ ${attached.length} prêt(s) lié(s) seront supprimés (${active.length} actif(s), dont ${upcoming.length} à venir).`;
      const preview = attached.slice(0, 3).map(l => `• ${l.borrower_name || "?"} — ${l.start_date} → ${l.end_date}`).join("\n");
      msg += `\n\n${preview}`;
      if (attached.length > 3) msg += `\n… et ${attached.length - 3} autre(s).`;
      msg += "\n\nUne entrée sera créée dans le Journal de suppressions et vous pourrez restaurer.";
    }
    if (!confirm(msg)) return;
    try {
      const { data } = await api.delete(`/equipment/${eq.id}`);
      toast.success(data?.cascade_loans ? `Supprimé (+ ${data.cascade_loans} prêt(s))` : "Supprimé");
      loadEquipment(); loadLoans();
    }
    catch (e) { toast.error(extractError(e)); }
  };

  // Journal des suppressions Matériel + Prêts
  const [journalOpen, setJournalOpen] = useState(false);
  const [journal, setJournal] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const loadJournal = async () => {
    setJournalLoading(true);
    try {
      const { data } = await api.get("/equipment-deletions");
      setJournal(data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setJournalLoading(false); }
  };
  const openJournal = () => { setJournalOpen(true); loadJournal(); };
  const restoreEntry = async (entry) => {
    const label = entry.action === "equipment_delete"
      ? `Restaurer le matériel "${entry.equipment_name}" (+ ${entry.cascade_loans_count || 0} prêt(s)) ?`
      : `Restaurer le prêt de ${entry.borrower_name || "?"} (${entry.equipment_name}) ?`;
    if (!confirm(label)) return;
    try {
      await api.post(`/equipment-deletions/${entry.id}/restore`);
      toast.success("Restauré");
      loadJournal();
      loadEquipment();
      loadLoans();
    } catch (e) { toast.error(extractError(e)); }
  };

  const openNewLoan = (eqId = "", presetDate = null) => {
    setLoanForm({ ...EMPTY_LOAN, equipment_id: eqId, start_date: presetDate || "", end_date: presetDate || "" });
    setLoanEditingId(null);
    setLoanDialogOpen(true);
  };
  const openEditLoan = (l) => {
    setLoanForm({ ...EMPTY_LOAN, ...l, event_id: l.event_id || "" });
    setLoanEditingId(l.id);
    setLoanDialogOpen(true);
  };

  const saveLoan = async () => {
    // Ouvre une fenêtre synchrone (avant await) pour bypasser les popup blockers.
    // Elle sera pointée vers Google Calendar après la création, ou fermée en cas d'erreur / d'édition.
    const gcalWin = !loanEditingId ? window.open("about:blank", "_blank") : null;
    try {
      if (!loanForm.equipment_id) { toast.error("Sélectionnez un matériel"); if (gcalWin) gcalWin.close(); return; }
      if (!loanForm.start_date || !loanForm.end_date) { toast.error("Renseignez les dates"); if (gcalWin) gcalWin.close(); return; }
      if (loanForm.end_date < loanForm.start_date) { toast.error("La date de fin doit être après la date de début"); if (gcalWin) gcalWin.close(); return; }
      const payload = {
        ...loanForm,
        quantity: parseInt(loanForm.quantity) || 1,
        daily_rate: parseFloat(loanForm.daily_rate) || 0,
        event_id: loanForm.event_id || null,
      };
      if (loanEditingId) { await api.patch(`/loans/${loanEditingId}`, payload); toast.success("Prêt modifié"); }
      else {
        await api.post("/loans", payload);
        // Auto-ouverture de Google Calendar avec l'invitation à contact@antoineevenements.fr
        const eq = equipment.find(x => x.id === payload.equipment_id);
        const evLinked = events.find(x => x.id === payload.event_id);
        const url = buildLoanGoogleCalendarUrl({
          ...payload,
          equipment: eq ? { name: eq.name } : null,
          event_name: evLinked?.name || "",
        });
        if (gcalWin && url) {
          gcalWin.location.href = url;
          toast.success("Prêt créé — Google Agenda ouvert sur contact@antoineevenements.fr");
        } else if (url) {
          toast.success("Prêt créé — ouvrez l'onglet Google Agenda si le popup a été bloqué", { action: { label: "Ouvrir", onClick: () => window.open(url, "_blank") } });
        } else {
          toast.success("Prêt créé");
        }
      }
      setLoanDialogOpen(false);
      loadLoans();
    } catch (e) {
      if (gcalWin) gcalWin.close();
      toast.error(extractError(e));
    }
  };

  const removeLoan = async (l) => {
    if (!confirm(`Supprimer le prêt de "${l.borrower_name || "?"}" ?\n\nUne entrée sera créée dans le Journal — vous pourrez restaurer.`)) return;
    try { await api.delete(`/loans/${l.id}`); toast.success("Supprimé"); loadLoans(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const days = useMemo(() => monthGrid(monthDate), [monthDate]);
  const isSameMonth = (d) => d.getMonth() === monthDate.getMonth();
  const loansByDay = useMemo(() => {
    const map = {};
    loans.forEach(l => {
      const s = new Date(l.start_date + "T00:00:00");
      const e = new Date(l.end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d = addDays(d, 1)) {
        const key = toISODate(d);
        if (!map[key]) map[key] = [];
        map[key].push(l);
      }
    });
    return map;
  }, [loans]);

  const monthLabel = `${MONTHS_FR[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Matériel</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Inventaire & sorties</h1>
          <p className="text-sm text-muted-foreground mt-1">Gérez notre parc et suivez chaque sortie.</p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted">
          <TabsTrigger value="inventory" data-testid="tab-inventory"><Boxes className="h-4 w-4 mr-2" /> Inventaire</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar"><CalIcon className="h-4 w-4 mr-2" /> Calendrier</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher..." className="pl-9 h-10 w-56 bg-muted" data-testid="equipment-search" />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-52 h-10 rounded-full" data-testid="cat-filter"><SelectValue placeholder="Catégorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={openJournal} data-testid="equipment-journal-btn"><History className="h-4 w-4 mr-2" /> Journal</Button>
              <Button variant="outline" className="rounded-full" onClick={() => openNewLoan()} data-testid="new-loan-btn"><Plus className="h-4 w-4 mr-2" /> Prêt / Location</Button>
              <Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" onClick={openNewEq} data-testid="add-equipment-btn"><Plus className="h-4 w-4 mr-2" /> Ajouter du matériel</Button>
            </div>
          </div>

          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="py-3 px-5 font-medium">Nom</th>
                      <th className="py-3 px-5 font-medium">Catégorie</th>
                      <th className="py-3 px-5 font-medium text-right">Qté</th>
                      <th className="py-3 px-5 font-medium">État</th>
                      <th className="py-3 px-5 font-medium text-right">Valeur HT</th>
                      <th className="py-3 px-5 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.length === 0 && (
                      <tr><td colSpan={6} className="py-10 text-center text-muted-foreground"><Package className="h-8 w-8 mx-auto mb-2 opacity-50" />Aucun matériel</td></tr>
                    )}
                    {equipment.map(eq => {
                      const c = CONDITIONS.find(x => x.v === eq.condition) || CONDITIONS[1];
                      return (
                        <tr key={eq.id} className="border-b border-border/40 hover:bg-muted fade-up" data-testid={`equipment-row-${eq.id}`}>
                          <td className="py-3 px-5">
                            <div className="flex items-center gap-3">
                              {eq.photo_url ? (
                                <img src={eq.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-border/60" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center"><Package className="h-5 w-5 text-muted-foreground" /></div>
                              )}
                              <div>
                                <div className="font-medium cursor-pointer" onClick={() => openEditEq(eq)}>{eq.name}</div>
                                {eq.notes && <div className="text-xs text-muted-foreground truncate max-w-[280px]">{eq.notes}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-5 text-muted-foreground">{eq.category || "—"}</td>
                          <td className="py-3 px-5 text-right tabular-nums">{eq.quantity}</td>
                          <td className="py-3 px-5"><Badge className={`${c.cls} rounded-full border`}>{c.label}</Badge></td>
                          <td className="py-3 px-5 text-right tabular-nums">{eq.value ? `${Number(eq.value).toLocaleString("fr-FR")} €` : "—"}</td>
                          <td className="py-3 px-5 text-right">
                            <Button variant="ghost" size="icon" onClick={() => openNewLoan(eq.id)} data-testid={`loan-eq-${eq.id}`}><CalIcon className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditEq(eq)} data-testid={`edit-eq-${eq.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => duplicateEq(eq)} title="Dupliquer" data-testid={`duplicate-eq-${eq.id}`}><Copy className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => removeEq(eq)} data-testid={`delete-eq-${eq.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="font-display font-semibold text-lg min-w-[180px] text-center capitalize" data-testid="cal-month">{monthLabel}</div>
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" className="rounded-full" onClick={() => setMonthDate(firstOfMonth(new Date()))} data-testid="cal-today">Aujourd'hui</Button>
            <Select value={calCatFilter} onValueChange={setCalCatFilter}>
              <SelectTrigger className="w-52 h-9 rounded-full ml-2" data-testid="cal-cat-filter"><SelectValue placeholder="Catégorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto"><Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 active:scale-95" onClick={() => openNewLoan()} data-testid="new-loan-btn"><Plus className="h-4 w-4 mr-2" /> Prêt / Location</Button></div>
          </div>

          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> Stock disponible à une date</h3>
                  <p className="text-xs text-muted-foreground">Choisissez une date, ou une période, pour voir la dispo minimale du parc (sorties actives déduites).</p>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <Label className="text-xs">Du</Label>
                    <Input type="date" value={stockDate} onChange={e => setStockDate(e.target.value)} className="h-9" data-testid="stock-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Au (optionnel)</Label>
                    <Input type="date" value={stockEndDate} onChange={e => setStockEndDate(e.target.value)} min={stockDate} className="h-9" data-testid="stock-end-date" />
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full mb-0.5" onClick={() => { setStockDate(toISODate(new Date())); setStockEndDate(""); }} data-testid="stock-today">Aujourd'hui</Button>
                  {stockEndDate && (
                    <Button variant="ghost" size="sm" className="rounded-full mb-0.5" onClick={() => setStockEndDate("")} data-testid="stock-clear-range">Date seule</Button>
                  )}
                </div>
              </div>
              {stockEndDate && stockEndDate >= stockDate && (
                <div className="text-[11px] text-muted-foreground">
                  Période analysée : <span className="font-semibold text-foreground">{stockDate}</span> → <span className="font-semibold text-foreground">{stockEndDate}</span>. Les colonnes « Sortis » et « Disponibles » montrent le <strong>pic de sorties</strong> sur la période (dispo la plus basse).
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="py-2 px-3 font-medium">Matériel</th>
                      <th className="py-2 px-3 font-medium">Catégorie</th>
                      <th className="py-2 px-3 font-medium text-right">Total</th>
                      <th className="py-2 px-3 font-medium text-right">Sortis</th>
                      <th className="py-2 px-3 font-medium text-right">Disponibles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockAtDate.length === 0 && (
                      <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-xs">Aucun matériel</td></tr>
                    )}
                    {stockAtDate.map(({ eq, out, available }) => (
                      <tr key={eq.id} className="border-b border-border/40" data-testid={`stock-row-${eq.id}`}>
                        <td className="py-2 px-3 font-medium">{eq.name}{(() => { const d = eqDimensionsLabel(eq); return d ? <span className="text-[11px] text-muted-foreground ml-2">({d})</span> : null; })()}</td>
                        <td className="py-2 px-3 text-xs text-muted-foreground">{eq.category || "—"}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{eq.quantity}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">{out}</td>
                        <td className="py-2 px-3 text-right">
                          <Badge className={`rounded-full font-semibold tabular-nums ${available === 0 ? "bg-destructive/10 text-destructive border-destructive/40" : available < eq.quantity ? "bg-accent/40 text-accent-foreground border-accent" : "bg-secondary text-secondary-foreground border-secondary"}`} data-testid={`stock-avail-${eq.id}`}>
                            {available}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-3">
              <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-muted-foreground mb-1 px-1">
                {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => <div key={d} className="text-center py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => {
                  const key = toISODate(d);
                  const dayLoans = loansByDay[key] || [];
                  const inMonth = isSameMonth(d);
                  const isToday = key === toISODate(new Date());
                  return (
                    <div key={i} className={`min-h-[100px] rounded-lg border p-1.5 ${inMonth ? "bg-card border-border/60" : "bg-muted border-transparent"} ${isToday ? "ring-2 ring-primary" : ""}`}>
                      <div className={`text-xs font-medium mb-1 ${inMonth ? "text-foreground" : "text-muted-foreground"}`}>{d.getDate()}</div>
                      <div className="space-y-1">
                        {dayLoans.slice(0, 3).map(l => (
                          <div key={l.id + key} onClick={() => openEditLoan(l)} className={`cursor-pointer text-[10px] leading-tight px-1.5 py-1 rounded truncate ${l.type === "location" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}
                               title={`${l.equipment?.name} — ${l.borrower_name}`} data-testid={`loan-chip-${l.id}`}>
                            {l.equipment?.name?.slice(0, 14) || "?"}
                          </div>
                        ))}
                        {dayLoans.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{dayLoans.length - 3}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-5">
              <h3 className="font-display text-lg font-semibold mb-3">Prêts & locations du mois</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="py-2 px-3 font-medium">Matériel</th>
                      <th className="py-2 px-3 font-medium">Type</th>
                      <th className="py-2 px-3 font-medium">Emprunteur</th>
                      <th className="py-2 px-3 font-medium">Période</th>
                      <th className="py-2 px-3 font-medium text-right">Qté</th>
                      <th className="py-2 px-3 font-medium text-right">€/jour</th>
                      <th className="py-2 px-3 font-medium">Statut</th>
                      <th className="py-2 px-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loans.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Aucun prêt ce mois-ci</td></tr>}
                    {loans.map(l => {
                      const st = LOAN_STATUS[l.status] || LOAN_STATUS.planned;
                      return (
                        <tr key={l.id} className="border-b border-border/40 hover:bg-muted fade-up" data-testid={`loan-row-${l.id}`}>
                          <td className="py-2 px-3 font-medium cursor-pointer" onClick={() => openEditLoan(l)}>{l.equipment?.name}</td>
                          <td className="py-2 px-3"><Badge className={`rounded-full border ${l.type === "location" ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-secondary-foreground border-secondary"}`}>{l.type === "location" ? "Location" : "Prêt"}</Badge></td>
                          <td className="py-2 px-3 text-muted-foreground">{l.borrower_name}{l.event_name ? ` · ${l.event_name}` : ""}</td>
                          <td className="py-2 px-3 text-muted-foreground tabular-nums">{fmtFR(l.start_date)} → {fmtFR(l.end_date)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{l.quantity}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{l.type === "location" && l.daily_rate ? `${Number(l.daily_rate).toLocaleString("fr-FR")} €` : "—"}</td>
                          <td className="py-2 px-3"><Badge className={`${st.cls} rounded-full border`}>{st.label}</Badge></td>
                          <td className="py-2 px-3 text-right">
                            {(() => {
                              const url = buildLoanGoogleCalendarUrl(l);
                              return url ? (
                                <a href={url} target="_blank" rel="noreferrer" title="Ajouter à l'agenda contact@antoineevenements.fr" data-testid={`loan-gcal-${l.id}`}>
                                  <Button variant="ghost" size="icon" className="text-primary hover:bg-primary/10">
                                    <CalIcon className="h-4 w-4" />
                                  </Button>
                                </a>
                              ) : null;
                            })()}
                            <Button variant="ghost" size="icon" onClick={() => openEditLoan(l)}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => removeLoan(l)}><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Equipment dialog */}
      <Dialog open={eqDialogOpen} onOpenChange={setEqDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{eqEditingId ? "Modifier le matériel" : "Nouveau matériel"}</DialogTitle></DialogHeader>

          {/* Champs communs à toutes les catégories */}
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-2 mb-1">Informations générales</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Nom *</Label><Input value={eqForm.name} onChange={e => setEqForm({ ...eqForm, name: e.target.value })} data-testid="eq-name" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={eqForm.category || ""} onValueChange={v => setEqForm({ ...eqForm, category: v, attributes: {} })}>
                <SelectTrigger data-testid="eq-category"><SelectValue placeholder="Sélectionner une catégorie…" /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  {/* Anciennes catégories libres — affichées si la valeur ne fait pas partie de la liste standard */}
                  {eqForm.category && !EQUIPMENT_CATEGORIES.includes(eqForm.category) && (
                    <SelectItem key={eqForm.category} value={eqForm.category}>{eqForm.category} (personnalisée)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>État</Label>
              <Select value={eqForm.condition} onValueChange={v => setEqForm({ ...eqForm, condition: v })}>
                <SelectTrigger data-testid="eq-condition"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Valeur d'achat HT (€)</Label><Input type="number" min="0" step="0.01" value={eqForm.value} onChange={e => setEqForm({ ...eqForm, value: e.target.value })} data-testid="eq-value" /></div>
            <div><Label>Fournisseur</Label><Input value={eqForm.supplier || ""} onChange={e => setEqForm({ ...eqForm, supplier: e.target.value })} placeholder="Nom du fournisseur…" data-testid="eq-supplier" /></div>
            <div><Label>Quantité</Label><Input type="number" min="1" value={eqForm.quantity} onChange={e => setEqForm({ ...eqForm, quantity: e.target.value })} data-testid="eq-quantity" /></div>
          </div>

          {/* Champs spécifiques à la catégorie */}
          {eqForm.category && CATEGORY_FIELDS[eqForm.category] && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-5 mb-1">
                Détails « {eqForm.category} »
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CATEGORY_FIELDS[eqForm.category].map(f => {
                  // Masque le champ conditionnel si son parent n'est pas coché
                  if (f.showIf && !((eqForm.attributes || {})[f.showIf])) return null;
                  const val = (eqForm.attributes || {})[f.key];
                  const colClass = f.colSpan === 2 ? "md:col-span-2" : "";
                  if (f.type === "checkbox") {
                    return (
                      <div key={f.key} className={`flex items-center gap-2 ${colClass}`}>
                        <Checkbox
                          id={`eq-attr-${f.key}`}
                          checked={!!val}
                          onCheckedChange={c => setAttr(f.key, !!c)}
                          data-testid={`eq-attr-${f.key}`}
                        />
                        <Label htmlFor={`eq-attr-${f.key}`} className="cursor-pointer font-normal">{f.label}</Label>
                      </div>
                    );
                  }
                  return (
                    <div key={f.key} className={colClass}>
                      <Label>{f.label}</Label>
                      <Input
                        type={f.type}
                        step={f.step}
                        value={val ?? ""}
                        onChange={e => setAttr(f.key, e.target.value)}
                        data-testid={`eq-attr-${f.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Notes — toujours en dernier */}
          <div className="mt-5">
            <Label>Notes</Label>
            <Textarea rows={3} value={eqForm.notes} onChange={e => setEqForm({ ...eqForm, notes: e.target.value })} data-testid="eq-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEqDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full active:scale-95 bg-primary" onClick={saveEq} data-testid="confirm-eq">{eqEditingId ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan dialog */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{loanEditingId ? "Modifier le prêt" : "Nouveau prêt / location"}</DialogTitle></DialogHeader>
          {!loanEditingId && (
            <div className="rounded-md border border-primary/30 bg-primary/5 text-primary text-xs px-3 py-2 flex items-start gap-2" data-testid="loan-gcal-hint">
              <CalIcon className="h-4 w-4 mt-0.5 shrink-0" />
              <span>À la création, un onglet <b>Google Agenda</b> s'ouvre automatiquement sur le compte <b>contact@antoineevenements.fr</b> (agenda commun AAE). Il ne reste qu'à cliquer <b>Enregistrer</b> dans Google.</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Matériel *</Label>
              <Select value={loanForm.equipment_id} onValueChange={v => setLoanForm({ ...loanForm, equipment_id: v })}>
                <SelectTrigger data-testid="loan-equipment"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>{equipment.map(eq => <SelectItem key={eq.id} value={eq.id}>{eq.name}{eq.category ? ` — ${eq.category}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={loanForm.type} onValueChange={v => setLoanForm({ ...loanForm, type: v })}>
                <SelectTrigger data-testid="loan-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pret">Prêt (gratuit)</SelectItem>
                  <SelectItem value="location">Location (payant)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={loanForm.status} onValueChange={v => setLoanForm({ ...loanForm, status: v })}>
                <SelectTrigger data-testid="loan-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LOAN_STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date de début *</Label><Input type="date" value={loanForm.start_date} onChange={e => setLoanForm({ ...loanForm, start_date: e.target.value })} data-testid="loan-start" /></div>
            <div><Label>Date de fin *</Label><Input type="date" value={loanForm.end_date} onChange={e => setLoanForm({ ...loanForm, end_date: e.target.value })} data-testid="loan-end" /></div>
            <div><Label>Quantité</Label><Input type="number" min="1" value={loanForm.quantity} onChange={e => setLoanForm({ ...loanForm, quantity: e.target.value })} data-testid="loan-qty" /></div>
            {loanForm.type === "location" && (
              <div><Label>€ / jour</Label><Input type="number" min="0" step="0.01" value={loanForm.daily_rate} onChange={e => setLoanForm({ ...loanForm, daily_rate: e.target.value })} data-testid="loan-rate" /></div>
            )}
            <div><Label>Emprunteur *</Label><Input value={loanForm.borrower_name} onChange={e => setLoanForm({ ...loanForm, borrower_name: e.target.value })} data-testid="loan-borrower" /></div>
            <div><Label>Contact (email / téléphone)</Label><Input value={loanForm.borrower_contact} onChange={e => setLoanForm({ ...loanForm, borrower_contact: e.target.value })} data-testid="loan-contact" /></div>
            <div className="md:col-span-2">
              <Label>Événement lié</Label>
              <Select value={loanForm.event_id || "none"} onValueChange={v => setLoanForm({ ...loanForm, event_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="loan-event"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {events.map(ev => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={loanForm.notes} onChange={e => setLoanForm({ ...loanForm, notes: e.target.value })} data-testid="loan-notes" /></div>
          </div>
          <DialogFooter>
            {loanEditingId && (
              <Button variant="ghost" className="text-destructive mr-auto" onClick={() => { const l = loans.find(x => x.id === loanEditingId); setLoanDialogOpen(false); removeLoan(l); }}>
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer
              </Button>
            )}
            {(() => {
              // Construit un objet loan enrichi (nom matériel) pour l'URL GCal
              const eq = equipment.find(x => x.id === loanForm.equipment_id);
              const evLinked = events.find(x => x.id === loanForm.event_id);
              const url = loanForm.start_date && loanForm.end_date ? buildLoanGoogleCalendarUrl({
                ...loanForm,
                equipment: eq ? { name: eq.name } : null,
                event_name: evLinked?.name || "",
              }) : null;
              return url ? (
                <a href={url} target="_blank" rel="noreferrer" data-testid="loan-dialog-gcal">
                  <Button type="button" variant="outline" className="rounded-full border-primary/40 text-primary hover:bg-primary/5">
                    <CalIcon className="h-4 w-4 mr-2" /> Ajouter à Google Agenda
                  </Button>
                </a>
              ) : null;
            })()}
            <Button variant="outline" className="rounded-full" onClick={() => setLoanDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full active:scale-95 bg-primary" onClick={saveLoan} data-testid="confirm-loan">{loanEditingId ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journal des suppressions Matériel + Prêts */}
      <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Journal des suppressions Matériel & Prêts</DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto space-y-2" data-testid="equipment-journal-list">
            {journalLoading && <div className="text-center text-muted-foreground py-8">Chargement…</div>}
            {!journalLoading && journal.length === 0 && (
              <div className="text-center text-muted-foreground py-8">Aucune suppression enregistrée.</div>
            )}
            {!journalLoading && journal.map(e => {
              const isEq = e.action === "equipment_delete";
              const done = !!e.restored_at;
              const when = e.timestamp ? new Date(e.timestamp).toLocaleString("fr-FR") : "";
              const by = e.by_user?.name || e.by_user?.email || "—";
              return (
                <div key={e.id} className={`rounded-lg border p-3 flex flex-col md:flex-row md:items-center gap-3 ${done ? "bg-muted border-border/40" : "border-border/60"}`} data-testid={`journal-row-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={isEq ? "bg-destructive/10 text-destructive border-destructive/30" : "bg-primary/10 text-primary border-primary/30"}>
                        {isEq ? "Matériel supprimé" : "Prêt supprimé"}
                      </Badge>
                      {done && <Badge className="bg-secondary text-secondary-foreground border-secondary">Restauré</Badge>}
                      <span className="text-xs text-muted-foreground">{when} · par {by}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium truncate">
                      {isEq
                        ? `${e.equipment_name || "?"} · ${e.equipment_category || "—"}${e.cascade_loans_count ? ` (+ ${e.cascade_loans_count} prêt${e.cascade_loans_count > 1 ? "s" : ""} lié${e.cascade_loans_count > 1 ? "s" : ""})` : ""}`
                        : `${e.borrower_name || "?"} · ${e.equipment_name || "?"} · ${e.start_date} → ${e.end_date}`}
                    </div>
                    {done && e.restored_by_user && (
                      <div className="text-xs text-muted-foreground mt-0.5">Restauré par {e.restored_by_user.name || e.restored_by_user.email} le {new Date(e.restored_at).toLocaleString("fr-FR")}</div>
                    )}
                  </div>
                  {!done && (
                    <Button size="sm" className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90" onClick={() => restoreEntry(e)} data-testid={`journal-restore-${e.id}`}>
                      <RotateCcw className="h-4 w-4 mr-2" /> Restaurer
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setJournalOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
